/**
 * bridge/clipboard_controller.js — "clipboard" bridge component (Stimulus + Strada).
 *
 * Custom-tier bridge component: copies a string (invoice number, payment link) to
 * the OS clipboard with native confirmation. Stimulus identifier "bridge--clipboard"
 * (glob-derived from this subdir); Strada component name "clipboard" MUST match the
 * iOS class var name (native/ios/Chippie/Bridge/ClipboardComponent.swift) and the
 * Android-reached method name (window.ChippieNative.copyToClipboard).
 *
 * Why it extends Strada's BridgeComponent: the iOS pasteboard write is reached via
 * this.send("copy", ...), which only works on a connected bridge component inside the
 * iOS Hotwire Native shell. Extending BridgeComponent gives us that send() transport
 * AND the UA-gated connect() (it only connects in a native shell).
 *
 * Three-way transport: rather than each caller branching on platform, this controller
 * registers a single copy() impl set onto the window.ChippieBridge facade
 * ({ ios, android, pwa }). The facade (chippie-bridge.js) owns platform detection and
 * picks the right impl per call. Callers use window.ChippieBridge.clipboard(text).
 *   - ios:     this.send("copy", { text, confirmation, confirmationText }) over Strada
 *              to ClipboardComponent.swift (writes UIPasteboard + shows confirmation)
 *   - android: window.ChippieNative.copyToClipboard(text) (@JavascriptInterface;
 *              ClipboardManager + a Toast confirmation)
 *   - pwa:     navigator.clipboard.writeText(text), falling back to the legacy
 *              document.execCommand("copy") path when the async API is unavailable
 *              (insecure contexts / older WebViews). Confirmation on the PWA is the
 *              caller's responsibility (no native toast here).
 *
 * Android + PWA impls are platform-static (no per-instance state), so they are
 * registered once at module load via registerStandalone() — clipboard() works on
 * those platforms even with no <element data-controller> on the page. The iOS impl
 * needs a live bridge instance for this.send, so connect() overrides it while the
 * controller is connected (i.e. inside the iOS shell) and disconnect() reverts.
 *
 * Template wiring (either path):
 *   <button data-controller="bridge--clipboard"
 *           data-bridge--clipboard-text-value="{{ invoice.number }}"
 *           data-action="bridge--clipboard#copy">Copy invoice number</button>
 * Or call window.ChippieBridge.clipboard("INV-123") directly from any JS.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// PWA copy: prefer the async Clipboard API; fall back to a hidden textarea +
// execCommand for insecure contexts / older WebViews. Returns a Promise<boolean>.
function pwaCopy(text) {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
    }
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return Promise.resolve(ok);
    } catch (_e) {
        return Promise.resolve(false);
    }
}

// Android + PWA impls carry no per-instance state, so register them at module load.
// The facade is the SSOT for which one runs per call.
function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("clipboard", {
        // ios is overridden by the controller's connect() with a real send; this
        // placeholder is a safe no-op if somehow selected before connect.
        ios: () => {},
        android: (text) => {
            if (window.ChippieNative && window.ChippieNative.copyToClipboard) {
                try { window.ChippieNative.copyToClipboard(String(text)); } catch (_e) { /* bridge gone */ }
            }
        },
        pwa: (text) => pwaCopy(String(text)),
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "clipboard";

    static values = {
        text: String,
        confirmationText: { type: String, default: "Copied" },
    };

    connect() {
        super.connect();
        // Inside the iOS shell now: wire the real Strada send into the facade,
        // keeping the Android/PWA fallbacks intact.
        if (!window.ChippieBridge || !window.ChippieBridge.register) return;
        window.ChippieBridge.register("clipboard", {
            ios: (text, confirmationText) =>
                this.send("copy", {
                    text: String(text),
                    confirmation: true,
                    confirmationText: confirmationText || "Copied",
                }),
            android: (text) => {
                if (window.ChippieNative && window.ChippieNative.copyToClipboard) {
                    try { window.ChippieNative.copyToClipboard(String(text)); } catch (_e) { /* bridge gone */ }
                }
            },
            pwa: (text) => pwaCopy(String(text)),
        });
    }

    disconnect() {
        // Leaving the iOS shell context: drop the live-send impl back to the
        // standalone placeholder so a stale this.send is never invoked.
        registerStandalone();
        super.disconnect();
    }

    // data-action="bridge--clipboard#copy" trigger — copies this element's text.
    copy(event) {
        if (event) event.preventDefault();
        window.ChippieBridge.clipboard(this.textValue, this.confirmationTextValue);
    }
}
